import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { MdArrowForward, MdCheck, MdClose, MdMenu } from 'react-icons/md';
import settingsAPI from '../../services/settings';
import { eventTiming, formatEventDate, formatEventTimeRange } from './landingFormat';
import { getViewEmployerHref } from '../../utils/viewEmployerLink';
import { EImage, EList, ELink, EText, Heading, SmartLink, fieldId, getAt } from './homeEditables';

// The home page itself, shared by the public page (LandingPage) and the SuperAdmin editor
// canvas (LandingPageEditor), so what SuperAdmin edits is exactly what visitors get.
// `level` is the heading level of the main title: 1 on the public page, 2 inside the editor.

export const BLOCK_LABELS = {
  trusted: 'Trust line',
  audiences: 'Job seeker and employer cards',
  events: 'Upcoming job fairs',
  booths: 'Employers',
  organizations: 'Organizations'
};

// Section ids used by menu links (#upcoming-events …)
export const BLOCK_ANCHORS = { events: 'upcoming-events', booths: 'employers', organizations: 'organizations' };

const initials = (name = '') => name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase();

/** A logo next to the visible name repeats it, so it is decorative (alt=""). */
function CardLogo({ url, name }) {
  const [failed, setFailed] = useState(false);
  if (url && !failed) return <img src={url} alt="" className="hp-card-logo" onError={() => setFailed(true)} />;
  return <span className="hp-card-logo hp-card-logo--initials" aria-hidden="true">{initials(name)}</span>;
}

function NumberSetting({ ctx, path, label, min, max }) {
  const id = fieldId(path);
  const error = ctx.errorFor(path);
  return (
    <div className="hpe-setting">
      <label htmlFor={id}>{label}</label>
      <input
        id={id}
        type="number"
        inputMode="numeric"
        min={min}
        max={max}
        value={getAt(ctx.content, path) ?? ''}
        onChange={(e) => ctx.onChange(path, e.target.value === '' ? '' : Number(e.target.value))}
        aria-invalid={error ? 'true' : undefined}
        aria-describedby={error ? `${id}-error` : undefined}
      />
      {error && <span id={`${id}-error`} className="hpe-error"><span className="sr-only">Error: </span>{error}</span>}
    </div>
  );
}

function CheckSetting({ ctx, path, label }) {
  const id = fieldId(path);
  return (
    <div className="hpe-setting hpe-setting--check">
      <input id={id} type="checkbox" checked={!!getAt(ctx.content, path)} onChange={(e) => ctx.onChange(path, e.target.checked)} />
      <label htmlFor={id}>{label}</label>
    </div>
  );
}

/* ---------------- Header ---------------- */

function Announcement({ ctx, nextEvent, level }) {
  const { announcement } = ctx.content;
  const mode = announcement.mode;
  if (!ctx.edit && (mode === 'off' || (mode === 'custom' && !announcement.text) || (mode === 'next-event' && !nextEvent))) {
    return null;
  }

  let body;
  if (mode === 'custom') {
    body = <EText ctx={ctx} path="announcement.text" label="Announcement text" className="hp-announce-text" multiline />;
  } else if (mode === 'next-event' && nextEvent) {
    const timing = eventTiming(nextEvent.start, nextEvent.end);
    body = (
      <p className="hp-announce-text">
        {formatEventDate(nextEvent.start)}: {nextEvent.name}, {formatEventTimeRange(nextEvent.start, nextEvent.end)}
        {' '}<span aria-hidden="true">—</span>{' '}
        <strong className="hp-announce-countdown">{timing.text}</strong>
      </p>
    );
  } else if (mode === 'next-event') {
    body = <p className="hp-announce-text">No job fair is scheduled, so visitors won't see this bar.</p>;
  } else {
    body = <p className="hp-announce-text">The bar is hidden from visitors.</p>;
  }

  return (
    <div className={`hp-announce${mode === 'off' ? ' hpe-muted' : ''}`}>
      <div className="hp-announce-inner">
        <Heading level={level + 1} className="sr-only">Announcement</Heading>
        {ctx.edit && (
          <fieldset className="hpe-radios" id={fieldId('announcement.mode')} tabIndex={-1}>
            <legend className="hpe-tag">The bar shows</legend>
            {[['next-event', 'Next job fair (automatic)'], ['custom', 'My own text'], ['off', 'Nothing (hidden)']].map(([value, text]) => (
              <span className="hpe-radio" key={value}>
                <input
                  type="radio"
                  id={`lp-announcement-mode-${value}`}
                  name="announcement-mode"
                  checked={mode === value}
                  onChange={() => ctx.onChange('announcement.mode', value)}
                />
                <label htmlFor={`lp-announcement-mode-${value}`}>{text}</label>
              </span>
            ))}
          </fieldset>
        )}
        {body}
        {mode !== 'off' && (
          <ELink ctx={ctx} path="announcement" name="Bar button" labelKey="linkLabel" urlKey="linkUrl"
            className="hp-btn hp-btn--primary hp-btn--small" />
        )}
      </div>
    </div>
  );
}

function NavLinksEditor({ ctx }) {
  const links = ctx.content.navLinks || [];
  const [pendingFocus, setPendingFocus] = useState(null);

  // Runs after the render that applied the change, so the target control exists
  useEffect(() => {
    if (!pendingFocus) return;
    document.getElementById(pendingFocus)?.focus();
    setPendingFocus(null);
  }, [pendingFocus]);

  const move = (index, delta) => {
    const target = index + delta;
    const next = [...links];
    [next[index], next[target]] = [next[target], next[index]];
    ctx.onChange('navLinks', next);
    const button = delta < 0 ? (target === 0 ? 'later' : 'earlier') : (target === next.length - 1 ? 'earlier' : 'later');
    setPendingFocus(`hpe-nav-${target}-${button}`);
    ctx.announce?.(`${next[target].label || 'Link'} moved to position ${target + 1} of ${next.length}.`);
  };
  const remove = (index) => {
    ctx.onChange('navLinks', links.filter((_, i) => i !== index));
    setPendingFocus('hpe-nav-add');
    ctx.announce?.(`${links[index].label || 'Link'} removed from the menu.`);
  };
  const add = () => {
    ctx.onChange('navLinks', [...links, { label: '', url: '' }]);
    setPendingFocus(fieldId(`navLinks.${links.length}.label`));
  };

  return (
    <div className="hpe-nav" id={fieldId('navLinks')} tabIndex={-1} role="group" aria-labelledby="hpe-nav-title">
      <span id="hpe-nav-title" className="hpe-tag">Menu links (up to 6)</span>
      {ctx.errorFor('navLinks') && <span className="hpe-error">{ctx.errorFor('navLinks')}</span>}
      <ol className="hpe-nav-list">
        {links.map((link, i) => (
          <li key={i} className="hpe-nav-item">
            <ELink ctx={ctx} path={`navLinks.${i}`} name={`Menu link ${i + 1}`} className="hp-nav-link" icon={false} />
            <div className="hpe-mini-actions">
              <button type="button" id={`hpe-nav-${i}-earlier`} className="hpe-mini" disabled={i === 0}
                onClick={() => move(i, -1)} aria-label={`Move menu link ${i + 1} earlier`}>←</button>
              <button type="button" id={`hpe-nav-${i}-later`} className="hpe-mini" disabled={i === links.length - 1}
                onClick={() => move(i, 1)} aria-label={`Move menu link ${i + 1} later`}>→</button>
              <button type="button" className="hpe-mini hpe-mini--danger" onClick={() => remove(i)}
                aria-label={`Remove menu link ${i + 1}`}>×</button>
            </div>
          </li>
        ))}
      </ol>
      <button type="button" id="hpe-nav-add" className="hpe-button" onClick={add} disabled={links.length >= 6}>
        + Add menu link
      </button>
    </div>
  );
}

export function HomeHeader({ ctx, nextEvent, user, level = 1, as: Tag = 'header', navLabel = 'Main' }) {
  const { content } = ctx;
  const [menuOpen, setMenuOpen] = useState(false);
  const [brandingLogo, setBrandingLogo] = useState('');
  const menuButtonRef = useRef(null);
  const ownLogo = content?.brand?.logoUrl;

  useEffect(() => {
    if (ownLogo) return undefined;
    let cancelled = false;
    Promise.resolve(settingsAPI.getSetting('branding_logo'))
      .then((res) => { if (!cancelled && res?.success && res.value) setBrandingLogo(res.value); })
      .catch(() => { /* the site name is shown as text */ });
    return () => { cancelled = true; };
  }, [ownLogo]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setMenuOpen(false);
        menuButtonRef.current?.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [menuOpen]);

  if (!content) {
    return <Tag className="hp-header"><div className="hp-topbar"><Link to="/login" className="hp-btn hp-btn--outline hp-login">Log in</Link></div></Tag>;
  }

  const siteName = content.brand.siteName || 'ABILITY Job Fair';
  const logoUrl = ownLogo || brandingLogo;
  const navLinks = content.navLinks || [];

  return (
    <Tag className="hp-header">
      <Announcement ctx={ctx} nextEvent={nextEvent} level={level} />
      <div className="hp-topbar">
        {ctx.edit ? (
          <div className="hpe-brand">
            <EText ctx={ctx} path="brand.siteName" label="Site name (read out as the logo link)" className="hp-brand-text" />
            <EImage ctx={ctx} path="brand.logoUrl" label="Header logo" className="hp-brand-logo"
              emptyText={brandingLogo ? 'Using the Branding logo on a white background. Upload a light logo for the dark header.' : 'No logo: the site name shows as text.'} />
          </div>
        ) : (
          <Link to="/" className="hp-brand">
            {logoUrl ? (
              <img src={logoUrl} alt={`${siteName} home`} className={`hp-brand-logo${ownLogo ? '' : ' hp-brand-logo--chip'}`} />
            ) : (
              <span className="hp-brand-text">{siteName}<span className="sr-only"> home</span></span>
            )}
          </Link>
        )}
        <nav className="hp-nav" aria-label={navLabel}>
          {ctx.edit ? <NavLinksEditor ctx={ctx} /> : (
            <>
              {navLinks.length > 0 && (
                <button
                  type="button"
                  ref={menuButtonRef}
                  className="hp-menu-button"
                  aria-expanded={menuOpen}
                  aria-controls="hp-nav-list"
                  onClick={() => setMenuOpen((open) => !open)}
                >
                  {menuOpen ? <MdClose aria-hidden="true" /> : <MdMenu aria-hidden="true" />}
                  <span>Menu</span>
                </button>
              )}
              <ul id="hp-nav-list" className={`hp-nav-list${menuOpen ? ' is-open' : ''}`}>
                {navLinks.map((item, i) => (
                  <li key={`${i}-${item.url}`}>
                    <SmartLink url={item.url} className="hp-nav-link" onClick={() => setMenuOpen(false)}>{item.label}</SmartLink>
                  </li>
                ))}
              </ul>
            </>
          )}
          {user ? (
            <Link to="/dashboard" className="hp-btn hp-btn--outline hp-login">Go to dashboard</Link>
          ) : (
            <Link to="/login" className="hp-btn hp-btn--outline hp-login">Log in</Link>
          )}
        </nav>
      </div>
    </Tag>
  );
}

/* ---------------- Body ---------------- */

function Hero({ ctx, nextEvent, level }) {
  const { hero } = ctx.content;
  const nextTiming = nextEvent ? eventTiming(nextEvent.start, nextEvent.end) : null;
  const hasAside = ctx.edit || !!hero.imageUrl || !!nextEvent;
  const parts = [hero.titleBefore, hero.titleHighlight, hero.titleAfter];

  const nextCard = nextEvent && (
    <div className="hp-next-card">
      <Heading level={level + 1} className="hp-next-label">Next job fair</Heading>
      <p className="hp-next-name">{nextEvent.name}</p>
      {nextEvent.organization && <p className="hp-next-meta">Hosted by {nextEvent.organization.name}</p>}
      <p className="hp-next-meta">
        <time dateTime={new Date(nextEvent.start).toISOString()}>{formatEventDate(nextEvent.start)}</time>
        <br />
        {formatEventTimeRange(nextEvent.start, nextEvent.end)}
      </p>
      <p className={`hp-badge${nextTiming.live ? ' hp-badge--live' : ''}`}>{nextTiming.text}</p>
      <Link to={`/event/${nextEvent.slug}/register`} className="hp-btn hp-btn--primary">
        Register<span className="sr-only"> for {nextEvent.name}</span>
        <MdArrowForward aria-hidden="true" className="hp-btn-icon" />
      </Link>
    </div>
  );

  return (
    <section className={`hp-hero${hasAside ? '' : ' hp-hero--single'}`} aria-labelledby={ctx.edit ? 'hpe-hero-label' : 'hp-title'}>
      {ctx.edit && <span id="hpe-hero-label" className="sr-only">Main banner</span>}
      <div className="hp-hero-text">
        <EText ctx={ctx} path="hero.eyebrow" label="Small heading" className="hp-eyebrow" />
        {ctx.edit ? (
          <div className="hp-title hpe-title">
            <EText ctx={ctx} path="hero.titleBefore" label="Main heading: start" as="span" multiline />
            <EText ctx={ctx} path="hero.titleHighlight" label="Main heading: highlighted" as="span" multiline className="hp-title-highlight" />
            <EText ctx={ctx} path="hero.titleAfter" label="Main heading: end" as="span" multiline />
          </div>
        ) : (
          <Heading level={level} id="hp-title" className="hp-title">
            {parts[0]}
            {parts[0] && parts[1] ? ' ' : ''}
            {parts[1] && <span className="hp-title-highlight">{parts[1]}</span>}
            {(parts[0] || parts[1]) && parts[2] ? ' ' : ''}
            {parts[2]}
          </Heading>
        )}
        <EText ctx={ctx} path="hero.body" label="Introduction" className="hp-hero-body" multiline />
        <div className="hp-hero-actions">
          <ELink ctx={ctx} path="hero.primaryCta" name="First button" className="hp-btn hp-btn--primary hp-btn--large" />
          <ELink ctx={ctx} path="hero.secondaryCta" name="Second button" className="hp-btn hp-btn--outline hp-btn--large" />
        </div>
      </div>
      {hasAside && (
        <div className={`hp-hero-media${!hero.imageUrl && !ctx.edit ? ' hp-hero-media--card' : ''}`}>
          {ctx.edit ? (
            <>
              <EImage ctx={ctx} path="hero.imageUrl" label="Banner image" altPath="hero.imageAlt"
                decorativePath="hero.imageDecorative" className="hp-hero-image"
                emptyText="No banner image: visitors see the next job fair card below instead." />
              {!hero.imageUrl && nextCard}
            </>
          ) : hero.imageUrl ? (
            <img src={hero.imageUrl} alt={hero.imageDecorative ? '' : hero.imageAlt} className="hp-hero-image" />
          ) : nextCard}
        </div>
      )}
    </section>
  );
}

function Audiences({ ctx, level }) {
  return (
    <div className="hp-audiences">
      {ctx.content.audiences.map((a, i) => {
        const base = `audiences.${i}`;
        const headingId = `${a.id}-heading`;
        return (
          <section key={a.id} id={a.id} className="hp-panel" aria-labelledby={ctx.edit ? undefined : headingId}
            aria-label={ctx.edit ? (i === 0 ? 'Job seeker card' : 'Employer card') : undefined}>
            <EText ctx={ctx} path={`${base}.label`} label="Label" className="hp-panel-label" />
            {ctx.edit ? (
              <EText ctx={ctx} path={`${base}.heading`} label="Heading" className="hp-panel-heading" />
            ) : (
              <Heading level={level + 1} id={headingId} className="hp-panel-heading">{a.heading}</Heading>
            )}
            <EText ctx={ctx} path={`${base}.body`} label="Text" className="hp-panel-body" multiline />
            {ctx.edit ? <EList ctx={ctx} path={`${base}.bullets`} label="Points" /> : (
              a.bullets.filter(Boolean).length > 0 && (
                <ul className="hp-checklist">
                  {a.bullets.filter(Boolean).map((b, j) => (
                    <li key={`${j}-${b}`}><MdCheck aria-hidden="true" className="hp-check-icon" />{b}</li>
                  ))}
                </ul>
              )
            )}
            <ELink ctx={ctx} path={base} name="Button" labelKey="ctaLabel" urlKey="ctaUrl" className="hp-btn hp-btn--outline" />
          </section>
        );
      })}
    </div>
  );
}

function ListingSection({ ctx, blockKey, level, count, total, noun, empty, settings, children }) {
  const id = BLOCK_ANCHORS[blockKey];
  const base = `sections.${blockKey}`;
  const heading = getAt(ctx.content, `${base}.heading`);
  return (
    <section id={id} className="hp-section" aria-labelledby={`${id}-heading`}>
      {ctx.edit ? (
        <>
          <span id={`${id}-heading`} className="sr-only">{BLOCK_LABELS[blockKey]}</span>
          <EText ctx={ctx} path={`${base}.heading`} label="Heading" className="hp-section-heading" />
          <div className="hpe-settings" role="group" aria-label={`${BLOCK_LABELS[blockKey]} settings`}>{settings}</div>
        </>
      ) : (
        <Heading level={level + 1} id={`${id}-heading`} className="hp-section-heading">{heading}</Heading>
      )}
      <EText ctx={ctx} path={`${base}.intro`} label="Introduction" className="hp-section-intro" multiline />
      {count === 0 ? <p className="hp-empty">{empty}</p> : children}
      {total > count && count > 0 && <p className="hp-section-more">Showing {count} of {total} {noun}.</p>}
    </section>
  );
}

function EventCard({ event, level }) {
  const timing = eventTiming(event.start, event.end);
  const headingId = `hp-event-${event.id}`;
  return (
    <li>
      <article className="hp-card hp-event-card" aria-labelledby={headingId}>
        <div className="hp-card-top">
          <CardLogo url={event.logoUrl} name={event.name} />
          <Heading level={level} id={headingId} className="hp-card-title">{event.name}</Heading>
        </div>
        <p className={`hp-badge${timing.live ? ' hp-badge--live' : ''}`}>{timing.text}</p>
        {event.organization && <p className="hp-card-meta">Hosted by {event.organization.name}</p>}
        <p className="hp-card-meta">
          <time dateTime={new Date(event.start).toISOString()}>{formatEventDate(event.start)}</time>
          <br />
          {formatEventTimeRange(event.start, event.end)}
        </p>
        {event.boothCount > 0 && (
          <p className="hp-card-meta">{event.boothCount} {event.boothCount === 1 ? 'employer' : 'employers'}</p>
        )}
        <div className="hp-card-actions">
          <Link to={`/event/${event.slug}/register`} className="hp-btn hp-btn--primary">
            Register<span className="sr-only"> for {event.name}</span>
          </Link>
          {event.link && (
            <a href={event.link} className="hp-text-link">Event website<span className="sr-only"> for {event.name}</span></a>
          )}
        </div>
      </article>
    </li>
  );
}

export function HomeBody({ ctx, data, level = 1 }) {
  const { content } = ctx;
  const { sections } = content;
  const events = (data.events || []).slice(0, sections.events.maxItems || 1);
  const booths = (data.booths || []).slice(0, sections.booths.maxItems || 1);
  const organizations = (data.organizations || [])
    .filter((o) => !sections.organizations.onlyWithEvents || o.upcomingEventCount > 0);

  const blocks = {
    trusted: () => <EText ctx={ctx} path="trustedByText" label="Trust line" className="hp-trusted" />,
    audiences: () => <Audiences ctx={ctx} level={level} />,
    events: () => (
      <ListingSection ctx={ctx} blockKey="events" level={level} count={events.length}
        total={data.totalEvents ?? events.length} noun="job fairs"
        empty="No job fairs are scheduled right now. Please check back soon."
        settings={<NumberSetting ctx={ctx} path="sections.events.maxItems" label="Show up to (1 to 24)" min={1} max={24} />}>
        <ul className="hp-grid hp-grid--events">
          {events.map((event) => <EventCard key={event.id} event={event} level={level + 2} />)}
        </ul>
      </ListingSection>
    ),
    booths: () => (
      <ListingSection ctx={ctx} blockKey="booths" level={level} count={booths.length}
        total={data.totalBooths ?? booths.length} noun="employers"
        empty="Employers will be listed here once they have booths at an upcoming job fair."
        settings={<NumberSetting ctx={ctx} path="sections.booths.maxItems" label="Show up to (1 to 60)" min={1} max={60} />}>
        <ul className="hp-grid hp-grid--booths">
          {booths.map((booth) => (
            <li key={booth.id}>
              <Link to={getViewEmployerHref(booth, booth.event.slug)} className="hp-card hp-booth-card">
                <CardLogo url={booth.logoUrl} name={booth.name} />
                <span className="hp-booth-name">{booth.name}</span>
                <span className="hp-card-meta">
                  At {booth.event.name}{booth.eventCount > 1 ? ` and ${booth.eventCount - 1} more` : ''}
                </span>
                <span className="hp-booth-cta">View employer<MdArrowForward aria-hidden="true" className="hp-btn-icon" /></span>
              </Link>
            </li>
          ))}
        </ul>
      </ListingSection>
    ),
    organizations: () => (
      <ListingSection ctx={ctx} blockKey="organizations" level={level} count={organizations.length}
        total={organizations.length} noun="organizations" empty="Host organizations will be listed here soon."
        settings={<CheckSetting ctx={ctx} path="sections.organizations.onlyWithEvents" label="Only organizations with an upcoming job fair" />}>
        <ul className="hp-grid hp-grid--orgs">
          {organizations.map((org) => (
            <li key={org.id}>
              <article className="hp-card hp-org-card" aria-labelledby={`hp-org-${org.id}`}>
                <div className="hp-card-top">
                  <CardLogo url={org.logoUrl} name={org.name} />
                  <Heading level={level + 2} id={`hp-org-${org.id}`} className="hp-card-title">{org.name}</Heading>
                </div>
                {org.description && <p className="hp-card-body">{org.description}</p>}
                <p className="hp-card-meta">
                  {org.upcomingEventCount === 0
                    ? 'No upcoming job fairs'
                    : `${org.upcomingEventCount} upcoming ${org.upcomingEventCount === 1 ? 'job fair' : 'job fairs'}`}
                </p>
              </article>
            </li>
          ))}
        </ul>
      </ListingSection>
    )
  };

  return (
    <>
      <Hero ctx={ctx} nextEvent={data.nextEvent} level={level} />
      {(content.blocks || [])
        .filter((b) => b.visible && blocks[b.key])
        .map((b) => (
          <div key={b.key} className="hp-block" data-block={b.key} id={ctx.edit ? `hpe-block-${b.key}` : undefined}>
            {blocks[b.key]()}
          </div>
        ))}
    </>
  );
}
